import { ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

export function Modal({ open, onClose, title, children, footer, size = 'md' }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const widths: Record<string, string> = {
    sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl', '2xl': 'max-w-6xl',
  };

  return (
    // dvh (динамическая высота вьюпорта) вместо vh — иначе на iPhone нижняя панель Safari
    // перекрывает низ окна и прячет кнопку «Сохранить». Нижний отступ safe-area — на тот же
    // случай. overflow-x-hidden на слое — чтобы окно нельзя было «свайпнуть» вбок на iOS,
    // когда что-то внутри чуть шире экрана (на Android этого нет, на iPhone уезжало).
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-x-hidden animate-in"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className={cn('relative w-full min-w-0 panel shadow-pop animate-pop flex flex-col max-h-[90dvh] overflow-hidden', widths[size])}>
        <div className="flex items-center justify-between px-5 h-14 border-b border-line flex-shrink-0">
          <h2 className="text-base font-semibold text-ink-1 truncate pr-2">{title}</h2>
          <button className="icon-btn flex-shrink-0" onClick={onClose} aria-label="Закрыть">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto overflow-x-hidden flex-1 min-w-0">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 h-14 border-t border-line bg-surface-2/40 rounded-b-xl2 flex-shrink-0">{footer}</div>}
      </div>
    </div>
  );
}
